import React, { useState } from 'react';
import Icon from './Icon';
// Reuse the AddContactModal shell styles (overlay / modal / sections /
// footer) so the two "to OnPoint" modals look identical. The before →
// after diff is the only bit unique to this modal and uses small inline
// styles rather than a second CSS module.
import styles from './AddContactModal.module.css';

function capitaliseName(raw) {
  if (!raw) return '';
  return raw.trim().split(/\s+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// Shown when the sender's name near-matches an existing OnPoint contact
// whose email has changed. The user reviews the before/after and either
// confirms the email update or bails out to "add as a new contact".
export default function UpdateContactModal({ message, senderInfo, onClose, onUpdated, onSwitchToAdd }) {
  const candidate = senderInfo?.updateCandidate;
  const customer  = senderInfo?.customer;

  const oldEmail = candidate?.currentEmail || '';
  const newEmail = (message?.from || '').trim().toLowerCase();
  const [name,   setName]   = useState(
    capitaliseName(message?.fromName || candidate?.contactName || newEmail.split('@')[0])
  );
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  if (!candidate || !customer) return null;

  const nameChanged = capitaliseName(candidate.contactName) !== capitaliseName(name);

  async function handleConfirm() {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/mail/update-contact', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          customerId: customer.id,
          oldEmail,
          newEmail,
          name: capitaliseName(name),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update');
      onUpdated(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.titleBar}>
          <span className={styles.title}>Update contact in OnPoint</span>
          <button className={styles.closeBtn} onClick={onClose} title="Close">
            <Icon name="x" size={13} />
          </button>
        </div>

        <div className={styles.body}>
          {/* Which contact we matched */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>
              Existing contact at {customer.customerName || 'this customer'}
            </div>
            <div className={styles.customerBadge}>
              <span className={styles.customerIcon}><Icon name="person" size={13} stroke={2.4} /></span>
              <span className={styles.customerName}>{candidate.contactName}</span>
            </div>
          </div>

          {/* Before → after */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Email change</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span style={{ minWidth: 52, color: '#8a94a6' }}>Before</span>
                <span style={{ textDecoration: 'line-through', color: '#b04a4a' }}>{oldEmail}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span style={{ minWidth: 52, color: '#8a94a6' }}>After</span>
                <span style={{ color: '#2f7d4f', fontWeight: 600 }}>{newEmail}</span>
              </div>
            </div>
          </div>

          {/* Editable name — defaults to the sender's display name */}
          <div className={styles.section}>
            <div className={styles.field}>
              <label className={styles.label}>Contact name</label>
              <input className={styles.input} value={name}
                onChange={e => setName(e.target.value)}
                onBlur={e => setName(capitaliseName(e.target.value))} />
              {nameChanged && (
                <span style={{ fontSize: 11, color: '#8a94a6', marginTop: 4, display: 'block' }}>
                  OnPoint currently has “{candidate.contactName}” — confirming updates the name too.
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={onSwitchToAdd}
              style={{
                background: 'none', border: 'none', padding: 0, marginTop: 10,
                color: '#5b6678', fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
              }}
            >
              Not the same person? Add as a new contact instead
            </button>
          </div>
        </div>

        {error && <div className={styles.error}>⚠ {error}</div>}

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button className={styles.saveBtn} onClick={handleConfirm} disabled={saving}>
            {saving ? 'Updating…' : 'Confirm update'}
          </button>
        </div>
      </div>
    </div>
  );
}
